/*
 * Creative Coverage explorer — framework-free, mountable port of the proven
 * standalone artifact (U6 of the Creative Analysis plan).
 *
 * The module owns ALL DOM inside the element it creates in the given
 * container; React (U7) owns the shell around it and mounts/unmounts via
 * `mountExplorer(...).destroy()`. Three deliberate departures from the
 * reference implementation, per the plan:
 *
 *   1. No hash read/write — initial state comes from `opts.initialState` and
 *      every view-state change is reported through `opts.onStateChange`
 *      (the page owns the URL searchParams round-trip).
 *   2. No staleness/no-sheet banners — U7 renders all degraded-state banners
 *      from snapshot meta. The per-product sub-line info is kept.
 *   3. XSS hardening — the reference interpolated data into innerHTML raw
 *      (fine for a private local file, not for stored data inside the app).
 *      Every data-sourced string goes through `escapeHtml`; ad/brief/doc
 *      links render as anchors only when they parse as http(s) URLs
 *      (otherwise the surrounding text renders without an anchor); thumbnail
 *      `src` must be a `data:image/...` URI or the image is dropped.
 *
 * Everything else — combined-product builder (£→$ @1.27), chart default +
 * grid toggle, tiles, dropdowns, filter chips, row search, trend arrows,
 * tooltip, sidebar, table view, show-all-rows, duo-credit person semantics —
 * is a behavior-faithful port.
 *
 * 2026-07-23 layout pass: the in-module Product select is gone (the page's
 * segmented control owns product switching — including the client-side "all"
 * combined view built by buildProducts).
 *
 * 2026-07-24 simplification pass (operator feedback: "3 selects confuse
 * people"): the default view is now RANKINGS — one "Rank by" dimension,
 * colour-coded toggleable metric chips, a grouped vertical bar chart
 * (thumbnail + name per group, one bar per active metric, each metric
 * scaled to its own max), and a ranking table below with best-cell
 * highlights and sortable metric columns. The Filter select + value chips
 * are gone (search still narrows groups). The coverage heat map lives
 * under the second tab with just Rows × Columns.
 */

import type {
  CoverageDimId,
  CoverageLivePriorRecord,
  CoverageLiveRecord,
  CoverageProduct,
  CoverageProducts,
  CoverageSheetRecord,
  ExplorerSegment,
  ViewState,
} from "./types";
import "./explorer.css";

export interface ExplorerOptions {
  /**
   * View state to restore (e.g. parsed from URL searchParams). Fields are
   * validated individually — unknown products/dims fall back to defaults,
   * mirroring the reference's tolerant hash parsing.
   */
  initialState?: Partial<ViewState>;
  /**
   * Called after every actual view-state change (product, view, pivot dims,
   * filter) — NOT on mount, and not for changes that leave the shareable
   * state untouched (row search, show-all, table toggle).
   */
  onStateChange?: (s: ViewState) => void;
}

export interface ExplorerHandle {
  /** Remove all module DOM and document-level listeners. Idempotent. */
  destroy(): void;
  /** Switch product programmatically — same semantics as the built-in select. */
  setProduct(id: string): void;
}

const DIMS: ReadonlyArray<readonly [CoverageDimId, string]> = [
  ["avatar", "Avatar"],
  ["awareness", "Awareness"],
  ["concept", "Concept / hook family"],
  ["headline", "Headline (live ads)"],
  ["angle", "Angle (category)"],
  ["problem_mechanism", "Problem mechanism"],
  ["solution_mechanism", "Solution mechanism"],
  ["visual_type", "Visual type"],
  ["format", "Format (IMG/VID/GIF)"],
  ["creator", "Copywriter"],
  ["designer", "Designer"],
  ["week", "Week"],
];

/** Below this window spend a cell is "thin" — shown faded, directional only. */
const MINSPEND = 150;
const AWARE_ORDER = [
  "Unaware",
  "Problem Aware",
  "Solution Aware",
  "Product Aware",
  "Most Aware",
  "Brand Aware",
];
const ROW_CAP = 24;
const COL_CAP = 12;
/** Groups shown in the grouped ranking chart (full list lives in the table). */
const RANK_CHART_CAP = 8;
/** Rows the ranking table shows before the show-all button. */
const TABLE_CAP = 30;
/** Standing FX rate for the client-side combined view (£ → $). */
const FX: Record<string, number> = { $: 1, "£": 1.27 };

/* ── security helpers ─────────────────────────────────────────────────── */

/** Escape a data-sourced string for interpolation into innerHTML. */
function escapeHtml(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Accept only absolute http(s) URLs — anything else renders as plain text. */
function safeUrl(v: string | undefined): string | null {
  if (!v) return null;
  try {
    const u = new URL(v);
    return u.protocol === "http:" || u.protocol === "https:" ? v : null;
  } catch {
    return null;
  }
}

/** Thumbnails are server-embedded data URIs; anything else is dropped. */
function safeThumb(v: string | undefined): string | null {
  return v && v.startsWith("data:image/") ? v : null;
}

/* ── shared pure helpers ──────────────────────────────────────────────── */

const canon = (v: string | undefined): string =>
  (v || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const fmtRatio = (v: number | null): string => (v == null ? "—" : v.toFixed(2));

function dimLabel(id: CoverageDimId): string {
  const d = DIMS.find((x) => x[0] === id);
  return d ? d[1] : id;
}

/** Chronological rank of a week label ("WK12" → 12); unknown labels sort last. */
function weekOrd(label: string): number {
  const m = /(\d+)/.exec(label);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

const dimOk = (k: unknown): k is CoverageDimId =>
  typeof k === "string" && DIMS.some((d) => d[0] === k);

function firstDimExcept(id: CoverageDimId): CoverageDimId {
  for (const d of DIMS) if (d[0] !== id) return d[0];
  return id; // unreachable — DIMS always has >1 entry
}

type AnyRecord = CoverageSheetRecord | CoverageLiveRecord | CoverageLivePriorRecord;

interface DimKey {
  k: string;
  label: string;
}

/**
 * Keys a record contributes to a dimension. Person dims are LISTS (duo
 * credits like dIBR/dYMA count under both people — prepared server-side as
 * arrays); all other dims are single values. Concept prefers the server's
 * canonical key `ck` (alias/V-adoption merged) over a raw canon of the label.
 */
function keysOf(rec: AnyRecord, dim: CoverageDimId): DimKey[] {
  if (dim === "creator" || dim === "designer") {
    return (rec.d[dim] || [])
      .map((v) => ({ k: canon(v), label: v }))
      .filter((x) => x.k);
  }
  if (dim === "concept") {
    const k = rec.ck || canon(rec.d.concept);
    return k ? [{ k, label: rec.d.concept || "" }] : [];
  }
  const v = rec.d[dim] as string | undefined;
  const k = canon(v);
  return k ? [{ k, label: v || "" }] : [];
}

interface AxisEntry {
  k: string;
  label: string;
  vol: number;
}

interface Cell {
  produced: number;
  w: number;
  l: number;
  concepts: CoverageSheetRecord[];
  sp: number;
  val: number;
  pu: number;
  /** Meta's own revenue (2026-10-28) — the "Meta ROAS" cross-check. */
  mval: number;
  /** Impressions + link clicks (2026-07-26) — the CTR metric's inputs. */
  im: number;
  lc: number;
  liveN: number;
  top: CoverageLiveRecord[];
  psp: number;
  pval: number;
  roas: number | null;
  vs: number | null;
  thin: boolean;
  proas: number | null;
  trend: string | null;
}

function newCell(): Cell {
  return {
    produced: 0,
    w: 0,
    l: 0,
    concepts: [],
    sp: 0,
    val: 0,
    pu: 0,
    mval: 0,
    im: 0,
    lc: 0,
    liveN: 0,
    top: [],
    psp: 0,
    pval: 0,
    roas: null,
    vs: null,
    thin: false,
    proas: null,
    trend: null,
  };
}

function finalizeCell(c: Cell, baseRoas: number): void {
  c.top.sort((a, b) => b.sp - a.sp);
  c.top = c.top.slice(0, 6);
  c.roas = c.sp ? c.val / c.sp : null;
  c.vs = c.roas !== null && baseRoas ? c.roas / baseRoas : null;
  c.thin = c.sp > 0 && c.sp < MINSPEND;
  // Trend needs ≥$50 prior spend to be meaningful.
  c.proas = c.psp >= 50 ? c.pval / c.psp : null;
  c.trend =
    c.sp > 0 && c.proas !== null && c.roas !== null
      ? c.roas >= c.proas * 1.15
        ? "↗"
        : c.roas <= c.proas * 0.85
          ? "↘"
          : "→"
      : null;
}

/** Diverging color bins for "payback vs account average". */
function fillFor(c: Cell): string | null {
  if (!c.produced && !c.sp) return null;
  if (!c.sp) return "idle";
  const v = c.vs;
  if (v == null) return "var(--mid)";
  if (v <= 0.5) return "var(--dn3)";
  if (v <= 0.8) return "var(--dn2)";
  if (v < 0.95) return "var(--dn1)";
  if (v < 1.05) return "var(--mid)";
  if (v < 1.2) return "var(--up1)";
  if (v < 1.5) return "var(--up2)";
  return "var(--up3)";
}

function inkFor(f: string): string {
  return f === "var(--dn3)" || f === "var(--up3)" ? "var(--pole-ink)" : "var(--ink)";
}

function sortByAwareness(list: AxisEntry[]): void {
  const ord = new Map(AWARE_ORDER.map((n, i) => [canon(n), i]));
  list.sort((a, b) => (ord.get(a.k) ?? 99) - (ord.get(b.k) ?? 99));
}

/**
 * Revenue source (2026-10-28): when a snapshot carries FunnelFlux money
 * (`meta.ff.status === "ok"`), `val`/`pu` become FunnelFlux revenue and
 * conversions on every record, Meta's own figures move to `mval`/`mpu`, and
 * the cold-traffic baseline is recomputed on FunnelFlux revenue. Everything
 * downstream (ROAS, payback shading, trends, tiles) then runs on FF money,
 * and never on a mix. Without FF money the records keep Meta's revenue.
 * Spend is Meta either way. The input is never mutated.
 */
export function applyFfMoney(p: CoverageProduct): CoverageProduct {
  // No FunnelFlux money: the product is returned AS IS (a missing `money`
  // reads as Meta, and `mval` falls back to `val` wherever it is summed).
  if (p.meta.ff?.status !== "ok") return p;
  const swap = <R extends CoverageLivePriorRecord>(r: R): R => ({
    ...r,
    mval: r.val,
    mpu: r.pu,
    val: r.fv ?? 0,
    pu: r.fpu ?? 0,
  });
  const live = p.live.map(swap);
  const livePrior = (p.live_prior || []).map(swap);
  const cold = live.filter((r) => !r.rt);
  const sp = cold.reduce((a, r) => a + r.sp, 0);
  const val = cold.reduce((a, r) => a + r.val, 0);
  const base = { roas: sp ? val / sp : 0, spend: p.base.spend };
  return { ...p, live, live_prior: livePrior, base, money: "ff" };
}

/**
 * Virtual "all products" view — built client-side so the payload isn't
 * duplicated. £ money converted to $ at the standing 1.27 rate so cells are
 * comparable; the label says so. The input object is never mutated.
 */
function buildProducts(raw: CoverageProducts): CoverageProducts {
  const data: CoverageProducts = {};
  for (const [k, p] of Object.entries(raw)) data[k] = applyFfMoney(p);
  const products: CoverageProducts = { ...data };
  if (Object.keys(data).length <= 1 || "all" in data) return products;
  // The combined view only uses FunnelFlux revenue when EVERY product has it;
  // otherwise it falls back to Meta revenue for all, never a mix.
  const allFf = Object.values(data).every((p) => p.money === "ff");
  const sheet: CoverageSheetRecord[] = [];
  const live: CoverageLiveRecord[] = [];
  const livePrior: CoverageLivePriorRecord[] = [];
  let spend = 0;
  let val = 0;
  let stale = 0;
  let sheets = 0;
  let unparsedW = 0;
  let unmatchedW = 0;
  let degraded = false;
  const warnings: string[] = [];
  const starts: string[] = [];
  const ends: string[] = [];
  for (const p of Object.values(data)) {
    const fx = FX[p.cur] || 1;
    sheet.push(...p.sheet);
    const conv = <R extends CoverageLivePriorRecord>(r: R): R => {
      const mval = Math.round((r.mval ?? r.val) * fx);
      return allFf
        ? { ...r, sp: Math.round(r.sp * fx), val: Math.round(r.val * fx), mval }
        : { ...r, sp: Math.round(r.sp * fx), val: mval, pu: r.mpu ?? r.pu, mval };
    };
    for (const r of p.live) live.push(conv(r));
    for (const r of p.live_prior || []) livePrior.push(conv(r));
    const pSpend = p.base.spend * fx;
    spend += pSpend;
    const pCold = p.live.filter((r) => !r.rt);
    const pColdSp = pCold.reduce((a, r) => a + r.sp, 0);
    const pColdVal = pCold.reduce((a, r) => a + (allFf ? r.val : (r.mval ?? r.val)), 0);
    val += pSpend * (pColdSp ? pColdVal / pColdSp : p.base.roas);
    unparsedW += p.meta.unparsed_spend_pct * pSpend;
    // `|| 0` / `|| []`: the jsonb passthrough may serve a pre-contract blob.
    unmatchedW += (p.meta.unmatched_spend_pct || 0) * pSpend;
    warnings.push(...(p.meta.warnings || []));
    if (p.meta.degraded) degraded = true;
    stale = Math.max(stale, p.meta.stale_days);
    sheets += p.meta.sheet_rows;
    starts.push(p.meta.start);
    ends.push(p.meta.end);
  }
  const end = ends.sort().slice(-1)[0] || "";
  products.all = {
    label: "All products (combined, £→$ @1.27)",
    cur: "$",
    base: { roas: spend ? val / spend : 0, spend },
    meta: {
      start: starts.sort()[0] || "",
      end,
      ad_edge: end,
      stale_days: stale,
      unparsed_spend_pct: spend ? unparsedW / spend : 0,
      unmatched_spend_pct: spend ? unmatchedW / spend : 0,
      sheet_rows: sheets,
      no_sheet: false,
      warnings,
      degraded,
    },
    sheet,
    live,
    live_prior: livePrior,
    money: allFf ? "ff" : "meta",
  };
  return products;
}

/* ── static shell (trusted markup — no data interpolated) ─────────────── */

const SHELL_HTML = `
<p class="sub subLine"></p>
<div class="controls">
  <span class="viewtabs"><button type="button" class="vChart">Rankings</button><button type="button" class="vGrid">Coverage grid</button></span>
  <label class="rowLbl">Rank by</label><select class="rowSel"></select>
  <span class="colWrap"><label>Columns</label><select class="colSel"></select></span>
  <input class="rowSearch" placeholder="Search…">
  <button type="button" class="chip rtChip"></button>
  <button type="button" class="chip chronoChip" style="display:none"></button>
</div>
<div class="chiprow metricChips"></div>
<div class="tiles" style="display:none"></div>
<div class="chart" style="display:none"></div>
<div class="legend">
  <span><span class="sw" style="background:var(--dn3)"></span>≤0.5× avg</span>
  <span><span class="sw" style="background:var(--dn2)"></span>0.5–0.8×</span>
  <span><span class="sw" style="background:var(--mid)"></span>0.8–1.05× ≈ avg</span>
  <span><span class="sw" style="background:var(--up1)"></span>1.05–1.2×</span>
  <span><span class="sw" style="background:var(--up2)"></span>1.2–1.5×</span>
  <span><span class="sw" style="background:var(--up3)"></span>≥1.5× avg</span>
  <span>·</span>
  <span><span class="sw" style="border-style:dashed"></span>never produced</span>
  <span><span class="sw"></span>produced, not running</span>
  <span>· faded = spend &lt; $150</span>
</div>
<div class="gridwrap"><table class="map"></table></div>
<div class="tblview"></div>
<button type="button" class="chip morebtn moreBtn" style="display:none"></button>
<p class="foot">Cells shade ROAS (revenue ÷ Meta spend) vs each product window's cold-traffic account average (gray ≈ average). Revenue is FunnelFlux per ad when the product's funnels are connected (labelled FF), otherwise Meta-attributed (labelled Meta); the two are never mixed. FunnelFlux can lag a day or two, so the newest days can read low. Deleted/archived ads are invisible to the reporting pipeline. Live dimensions parsed from ad names (incl. short/reused-name recovery); ads with no avatar+awareness parse only appear in pivots on dimensions they carry. Problem vs solution mechanism are separate fields (naming: …_Angle_ProblemMech_SolutionMech_Avatar_Awareness_…). Copywriter/Designer merge prefixed and bare codes (dMDA = MDA) and split duo credits — a dIBR/dYMA ad counts fully under BOTH people, so per-person totals can exceed account totals.</p>
<div class="tip"></div>
<div class="side">
  <button type="button" class="x sideX">×</button>
  <div class="sideBody"></div>
</div>
`;

/* ── mount ─────────────────────────────────────────────────────────────── */

export function mountExplorer(
  container: HTMLElement,
  data: CoverageProducts,
  opts: ExplorerOptions = {},
): ExplorerHandle {
  const products = buildProducts(data);
  const productIds = Object.keys(products);

  const root = document.createElement("div");
  root.className = "coverage-explorer";
  container.appendChild(root);

  if (productIds.length === 0) {
    // Degenerate input — U7 owns the real empty state; never crash here.
    root.innerHTML = '<p class="sub">No coverage data.</p>';
    return { destroy: () => root.remove(), setProduct: () => {} };
  }

  root.innerHTML = SHELL_HTML;

  function q<T extends Element = HTMLElement>(sel: string): T {
    const el = root.querySelector(sel);
    if (!el) throw new Error(`coverage-explorer: missing element ${sel}`);
    return el as T;
  }
  const subLine = q(".subLine");
  const tilesEl = q(".tiles");
  const chartEl = q(".chart");
  const metricChipsEl = q(".metricChips");
  const legendEl = q(".legend");
  const gridWrap = q(".gridwrap");
  const mapEl = q<HTMLTableElement>(".map");
  const moreBtn = q<HTMLButtonElement>(".moreBtn");
  const tblView = q(".tblview");
  const tipEl = q(".tip");
  const sideEl = q(".side");
  const sideBody = q(".sideBody");
  const vChart = q<HTMLButtonElement>(".vChart");
  const vGrid = q<HTMLButtonElement>(".vGrid");
  const rowLbl = q(".rowLbl");
  const colWrap = q(".colWrap");
  const rowSel = q<HTMLSelectElement>(".rowSel");
  const colSel = q<HTMLSelectElement>(".colSel");
  const rowSearch = q<HTMLInputElement>(".rowSearch");
  const rtChip = q<HTMLButtonElement>(".rtChip");
  const chronoChip = q<HTMLButtonElement>(".chronoChip");

  /* ── view state ── */
  let prod = productIds[0];
  let view: ViewState["view"] = "chart";
  let rowDim: CoverageDimId = "concept";
  let colDim: CoverageDimId = "awareness";
  let showAllRows = false;
  let rowQuery = "";
  /** Active ranking metrics (shareable since 2026-10-28 — saved reports). */
  const metricsOn = new Set<string>(["sp", "pu", "roas"]);
  /** Ranking-table sort column (a metric id). */
  let tableSort = "sp";
  /** Retargeting rows (2026-07-26 include/exclude; 2026-10-28 "only"):
   *  all = included (default), pros = excluded, ret = retargeting only. The
   *  account-average baseline is cold-only, except in "only" mode where it
   *  is the retargeting average (retargeting vs cold would always shade up). */
  let segment: ExplorerSegment = "all";
  /** Week dim only: sort groups chronologically instead of by spend. */
  let chronoWeeks = false;

  {
    const init = opts.initialState;
    if (init) {
      if (init.product && products[init.product]) prod = init.product;
      if (init.view === "grid" || init.view === "chart") view = init.view;
      if (dimOk(init.rowDim)) rowDim = init.rowDim;
      if (dimOk(init.colDim) && init.colDim !== rowDim) colDim = init.colDim;
      if (init.segment === "all" || init.segment === "pros" || init.segment === "ret") segment = init.segment;
      if (Array.isArray(init.metrics) && init.metrics.length > 0) {
        metricsOn.clear();
        for (const m of init.metrics) metricsOn.add(m);
      }
      if (typeof init.sort === "string" && init.sort) tableSort = init.sort;
      // filtDim/filtVal in a restored URL are from the pre-simplification UI
      // and are deliberately ignored (the Filter control is gone).
    }
    // Never pivot a dimension against itself (a colliding restore falls back
    // the same way the selects resolve collisions).
    if (colDim === rowDim) colDim = firstDimExcept(rowDim);
  }

  /* ── state reporting (replaces the reference's writeHash) ── */
  const stateOf = (): ViewState => ({
    product: prod,
    view,
    rowDim,
    colDim,
    filtDim: "",
    filtVal: "",
    metrics: [...metricsOn],
    sort: tableSort,
    segment,
  });
  let lastEmitted = JSON.stringify(stateOf()); // suppress the mount render
  function emitState(): void {
    const s = stateOf();
    const key = JSON.stringify(s);
    if (key === lastEmitted) return;
    lastEmitted = key;
    opts.onStateChange?.(s);
  }

  /* ── per-product accessors ── */
  const P = (): CoverageProduct => products[prod];
  const SHEET = (): CoverageSheetRecord[] => P().sheet;
  // The RT toggle filters live AND prior with the same scope, so trends
  // always compare like with like.
  const inSeg = (r: { rt?: boolean }): boolean =>
    segment === "all" ? true : segment === "ret" ? r.rt === true : r.rt !== true;
  const LIVE = (): CoverageLiveRecord[] => (segment === "all" ? P().live : P().live.filter(inSeg));
  const LIVE_PRIOR = (): CoverageLivePriorRecord[] => {
    const rows = P().live_prior || [];
    return segment === "all" ? rows : rows.filter(inSeg);
  };
  const BASE = (): { roas: number; spend: number } => {
    if (segment !== "ret") return P().base;
    const rt = P().live.filter((r) => r.rt === true);
    const spend = rt.reduce((a, r) => a + r.sp, 0);
    const val = rt.reduce((a, r) => a + r.val, 0);
    return { roas: spend ? val / spend : 0, spend };
  };
  /** Raw money text — pass through escapeHtml before HTML interpolation. */
  const fmtMoney = (v: number): string => P().cur + Number(v).toLocaleString();

  /**
   * The ranking metrics (2026-07-24 simplification pass) — fixed colours so
   * a metric is recognisable across the chips, the grouped bars, and the
   * table headers (the MagicBrief pattern). `highlight` marks metrics where
   * "highest = best" earns the green best-cell wash (spend is volume, not
   * quality, so it never highlights).
   */
  interface MetricDef {
    id: string;
    label: string;
    color: string;
    val: (c: Cell) => number | null;
    fmt: (v: number) => string;
    highlight: boolean;
    /** Hover tooltip on the column header (title attribute). */
    tip?: string;
  }
  // Every money label names its source (2026-10-28): spend is always Meta;
  // revenue is FunnelFlux when the snapshot has it, Meta otherwise.
  const ffMoney = products[prod]?.money === "ff";
  const METRICS: MetricDef[] = [
    { id: "sp", label: "Spend (Meta)", color: "#339af0", val: (c) => c.sp, fmt: (v) => fmtMoney(Math.round(v)), highlight: false },
    { id: "pu", label: ffMoney ? "Conversions (FF)" : "Purchases (Meta)", color: "#40c057", val: (c) => c.pu, fmt: (v) => v.toLocaleString(), highlight: true },
    // Label "ROAS" (2026-08-04, operator request — was the plain-language
    // "Back/$1"; the team's native term won).
    { id: "roas", label: ffMoney ? "ROAS (FF)" : "ROAS (Meta)", color: "#f59f00", val: (c) => c.roas, fmt: (v) => v.toFixed(2), highlight: true, tip: ffMoney ? "FunnelFlux revenue ÷ Meta spend." : "Meta-reported purchase value ÷ Meta spend. FunnelFlux revenue isn't connected for this product." },
    ...(ffMoney
      ? [{ id: "mroas", label: "Meta ROAS", color: "#adb5bd", val: (c: Cell) => (c.sp ? c.mval / c.sp : null), fmt: (v: number) => v.toFixed(2), highlight: false, tip: "Meta-reported purchase value ÷ Meta spend — a cross-check only." }]
      : []),
    // CTR (2026-07-26): link clicks / impressions — null (—) for snapshots
    // computed before im/lc shipped; a fresh recompute fills it.
    { id: "ctr", label: "CTR", color: "#22b8cf", val: (c) => (c.im > 0 ? (c.lc / c.im) * 100 : null), fmt: (v) => `${v.toFixed(2)}%`, highlight: true },
    { id: "live", label: "Live ads", color: "#845ef7", val: (c) => c.liveN, fmt: (v) => v.toLocaleString(), highlight: true },
    // Duo-credit (2026-07-29): a master-sheet row can credit two people
    // (c-/d- pair), so per-person Made totals can sum past the row count.
    { id: "made", label: "Made", color: "#f06595", val: (c) => c.produced, fmt: (v) => v.toLocaleString(), highlight: true, tip: "Launch rows credited in the master sheet. An ad credited to two people counts once for EACH of them, so personal totals can add up to more than the total ads made." },
  ];

  {
    const known = new Set(METRICS.map((m) => m.id));
    for (const id of [...metricsOn]) if (!known.has(id)) metricsOn.delete(id);
    if (metricsOn.size === 0) for (const id of ["sp", "pu", "roas"]) metricsOn.add(id);
    if (!known.has(tableSort)) tableSort = "sp";
  }

  function updateHeader(): void {
    const p = P();
    const m = p.meta;
    // Banners (stale pipeline, no sheet, snapshot age) are rendered by the
    // page from snapshot meta — the module keeps only the info sub-line.
    subLine.textContent =
      `Rank any creative dimension · produced (master sheet, ` +
      `${m.sheet_rows} launches) vs live performance ${m.start} → ${m.end} · ` +
      `${
        segment === "all"
          ? "incl. retargeting (account avg stays cold-only)"
          : segment === "pros"
            ? "cold traffic only"
            : "retargeting only (vs the retargeting average)"
      } · ` +
      `revenue: ${p.money === "ff" ? "FunnelFlux" : "Meta-reported"} · ` +
      `account average ROAS ${p.base.roas.toFixed(2)} ` +
      `on ${fmtMoney(Math.round(p.base.spend))} · ` +
      `${Math.round(m.unparsed_spend_pct)}% of spend has no avatar+awareness ` +
      `parse`;
  }

  /** The retargeting + week-order chips reflect their state on every render. */
  function updateControlChips(): void {
    rtChip.textContent =
      segment === "all" ? "Retargeting: included" : segment === "pros" ? "Retargeting: excluded" : "Retargeting only";
    rtChip.classList.toggle("on", segment !== "pros");
    chronoChip.textContent = chronoWeeks ? "Order: chronological" : "Order: by spend";
    chronoChip.classList.toggle("on", chronoWeeks);
    // Chronological ordering only means something on the Week dimension.
    chronoChip.style.display = view === "chart" && rowDim === "week" ? "" : "none";
  }

  interface GridAgg {
    cells: Map<string, Cell>;
    rowList: AxisEntry[];
    colList: AxisEntry[];
  }
  interface RowsAgg {
    cells: Map<string, Cell>;
    rowList: AxisEntry[];
  }

  function aggregate(): GridAgg {
    const cells = new Map<string, Cell>();
    const rows = new Map<string, AxisEntry>();
    const cols = new Map<string, AxisEntry>();
    const touch = (m: Map<string, AxisEntry>, k: string, label: string): AxisEntry => {
      const e = m.get(k) || { k, label, vol: 0 };
      if (!e.label) e.label = label;
      m.set(k, e);
      return e;
    };
    const cellOf = (rk: string, ck2: string): Cell => {
      const id = rk + "|" + ck2;
      let c = cells.get(id);
      if (!c) {
        c = newCell();
        cells.set(id, c);
      }
      return c;
    };
    for (const rec of SHEET()) {
      for (const rk of keysOf(rec, rowDim)) {
        for (const ck2 of keysOf(rec, colDim)) {
          touch(rows, rk.k, rk.label).vol += 1;
          touch(cols, ck2.k, ck2.label).vol += 1;
          const c = cellOf(rk.k, ck2.k);
          c.produced++;
          if (rec.rb === "w") c.w++;
          if (rec.rb === "l") c.l++;
          if (c.concepts.length < 14) c.concepts.push(rec);
        }
      }
    }
    for (const rec of LIVE()) {
      for (const rk of keysOf(rec, rowDim)) {
        for (const ck2 of keysOf(rec, colDim)) {
          touch(rows, rk.k, rk.label).vol += 1 + rec.sp / 100;
          touch(cols, ck2.k, ck2.label).vol += 1 + rec.sp / 100;
          const c = cellOf(rk.k, ck2.k);
          c.sp += rec.sp;
          c.val += rec.val;
          c.pu += rec.pu;
          c.mval += rec.mval ?? rec.val;
          c.im += rec.im ?? 0;
          c.lc += rec.lc ?? 0;
          c.liveN++;
          c.top.push(rec);
        }
      }
    }
    // Prior-window money folds into EXISTING cells only (prior-only cells
    // would be ghosts with nothing current to act on).
    for (const rec of LIVE_PRIOR()) {
      for (const rk of keysOf(rec, rowDim)) {
        for (const ck2 of keysOf(rec, colDim)) {
          const c = cells.get(rk.k + "|" + ck2.k);
          if (!c) continue;
          c.psp += rec.sp;
          c.pval += rec.val;
        }
      }
    }
    for (const c of cells.values()) finalizeCell(c, BASE().roas);
    const rowList = [...rows.values()].sort((a, b) => b.vol - a.vol);
    const colList = [...cols.values()].sort((a, b) => b.vol - a.vol);
    if (colDim === "awareness") sortByAwareness(colList);
    if (rowDim === "awareness") sortByAwareness(rowList);
    return { cells, rowList, colList };
  }

  /** Single-dimension aggregation for the chart view (columns ignored). */
  function aggregateRows(): RowsAgg {
    const cells = new Map<string, Cell>();
    const rows = new Map<string, AxisEntry>();
    const touch = (k: string, label: string): AxisEntry => {
      const e = rows.get(k) || { k, label, vol: 0 };
      if (!e.label) e.label = label;
      rows.set(k, e);
      return e;
    };
    const cellOf = (k: string): Cell => {
      let c = cells.get(k);
      if (!c) {
        c = newCell();
        cells.set(k, c);
      }
      return c;
    };
    for (const rec of SHEET()) {
      for (const rk of keysOf(rec, rowDim)) {
        touch(rk.k, rk.label).vol += 1;
        const c = cellOf(rk.k);
        c.produced++;
        if (rec.rb === "w") c.w++;
        if (rec.rb === "l") c.l++;
        if (c.concepts.length < 14) c.concepts.push(rec);
      }
    }
    for (const rec of LIVE()) {
      for (const rk of keysOf(rec, rowDim)) {
        touch(rk.k, rk.label).vol += 1 + rec.sp / 100;
        const c = cellOf(rk.k);
        c.sp += rec.sp;
        c.val += rec.val;
        c.pu += rec.pu;
        c.mval += rec.mval ?? rec.val;
        c.im += rec.im ?? 0;
        c.lc += rec.lc ?? 0;
        c.liveN++;
        c.top.push(rec);
      }
    }
    for (const rec of LIVE_PRIOR()) {
      for (const rk of keysOf(rec, rowDim)) {
        const c = cells.get(rk.k);
        if (!c) continue;
        c.psp += rec.sp;
        c.pval += rec.val;
      }
    }
    for (const c of cells.values()) finalizeCell(c, BASE().roas);
    const rowList = [...rows.values()].sort(
      (a, b) => (cells.get(b.k)?.sp || 0) - (cells.get(a.k)?.sp || 0),
    );
    return { cells, rowList };
  }

  let CUR: GridAgg | null = null;
  let CURR_ROWS: RowsAgg | null = null;
  /*
   * Aggregation cache (review fix B5). Aggregation is O(records × dims) while
   * row search (and show-all) is a POST-HOC filter over the already-aggregated
   * rows — so each view's aggregate is cached under a key of every input that
   * feeds it (product, rowDim, colDim; the view picks which cache slot, and
   * the mounted data itself never changes). A search keystroke or metric
   * toggle reuses the cached aggregate; any pivot/product change (incl.
   * setProduct) produces a new key and recomputes.
   */
  let curKey: string | null = null;
  let currRowsKey: string | null = null;
  const aggInputsKey = (): string => JSON.stringify([prod, rowDim, colDim, segment]);

  function gridAgg(): GridAgg {
    const key = aggInputsKey();
    if (!CUR || curKey !== key) {
      CUR = aggregate();
      curKey = key;
    }
    return CUR;
  }

  function rowsAgg(): RowsAgg {
    const key = aggInputsKey();
    if (!CURR_ROWS || currRowsKey !== key) {
      CURR_ROWS = aggregateRows();
      currRowsKey = key;
    }
    return CURR_ROWS;
  }

  function renderRankings(): void {
    const agg = rowsAgg();
    const { cells, rowList } = agg;
    // Tiles. Note: duo credits mean per-person sums can exceed account
    // totals by design (see the footer copy).
    let sp = 0;
    let val = 0;
    let pu = 0;
    let psp = 0;
    let pval = 0;
    let aboveSp = 0;
    let liveN = 0;
    for (const r of rowList) {
      const c = cells.get(r.k);
      if (!c) continue;
      sp += c.sp;
      val += c.val;
      pu += c.pu;
      liveN += c.liveN;
      psp += c.psp;
      pval += c.pval;
      if (c.vs !== null && c.vs >= 1.05) aboveSp += c.sp;
    }
    const roas = sp ? val / sp : 0;
    const proas = psp ? pval / psp : null;
    const rArrow =
      proas === null ? "" : roas >= proas * 1.15 ? " ↗" : roas <= proas * 0.85 ? " ↘" : " →";
    tilesEl.innerHTML = `
    <div class="tile"><div class="k">Spend (Meta)</div>
      <div class="v">${escapeHtml(fmtMoney(Math.round(sp)))}</div>
      <div class="s">prior ${escapeHtml(fmtMoney(Math.round(psp)))}</div></div>
    <div class="tile"><div class="k">${P().money === "ff" ? "ROAS (FF)" : "ROAS (Meta)"}</div>
      <div class="v">${roas.toFixed(2)}${rArrow}</div>
      <div class="s">${proas === null ? "no prior data" : "was " + proas.toFixed(2)}</div></div>
    <div class="tile"><div class="k">${P().money === "ff" ? "Conversions (FF)" : "Purchases (Meta)"}</div>
      <div class="v">${pu.toLocaleString()}</div>
      <div class="s">${liveN.toLocaleString()} live ads</div></div>
    <div class="tile"><div class="k">Spend above account avg</div>
      <div class="v">${sp ? Math.round((aboveSp / sp) * 100) : 0}%</div>
      <div class="s">in groups ≥1.05× payback</div></div>`;
    // Grouped bars (MagicBrief-style): top groups by spend, one colour-coded
    // bar per active metric, each metric scaled to its OWN max so different
    // units share the canvas. Thumbnail + name sit under each group.
    const on = METRICS.filter((m) => metricsOn.has(m.id));
    const qy = rowQuery.trim().toLowerCase();
    const pool = rowList.filter((r) => {
      const c = cells.get(r.k);
      return !!c && c.sp > 0 && (!qy || r.label.toLowerCase().includes(qy));
    });
    // Week dim + chronological order: oldest→newest, keeping the most RECENT
    // weeks when the cap bites (the near past matters most).
    const chrono = chronoWeeks && rowDim === "week";
    const shown = chrono
      ? [...pool].sort((a, b) => weekOrd(a.label) - weekOrd(b.label)).slice(-RANK_CHART_CAP)
      : pool.slice(0, RANK_CHART_CAP);
    const maxOf = new Map<string, number>();
    for (const m of on) {
      let mx = 0;
      for (const r of shown) {
        const c = cells.get(r.k);
        if (c) mx = Math.max(mx, m.val(c) ?? 0);
      }
      maxOf.set(m.id, mx);
    }
    let h = `<div class="rankHead">Comparing ${shown.length}: <span>${shown
      .map((r) => escapeHtml(r.label))
      .join(", ")}</span></div><div class="rankchart">`;
    for (const r of shown) {
      const c = cells.get(r.k);
      if (!c) continue;
      const topThumb = c.top.map((t) => safeThumb(t.th)).find((u) => u !== null) || null;
      h += `<div class="grp" data-r="${escapeHtml(r.k)}" title="${escapeHtml(r.label)} — click for detail"><div class="grpBars">`;
      for (const m of on) {
        const v = m.val(c);
        const mx = maxOf.get(m.id) || 0;
        const pct = v !== null && mx > 0 ? Math.max(3, Math.round((v / mx) * 100)) : 0;
        h += `<div class="vwrap"><div class="vlabel">${
          v === null ? "—" : escapeHtml(m.fmt(v))
        }</div><div class="vbar" style="height:${pct}%;background:${m.color}"></div></div>`;
      }
      // No-thumb ⇒ no box (2026-08-04): thumbnail downloads were dropped from
      // the sync, so an empty checkerboard would show on every group.
      h += `</div><div class="grpFoot">${
        topThumb ? `<img src="${escapeHtml(topThumb)}" alt="">` : ""
      }<span class="grpName">${escapeHtml(r.label)}</span></div></div>`;
    }
    h += "</div>";
    const prodOnly = rowList.filter((r) => {
      const c = cells.get(r.k);
      return !!c && !c.sp && c.produced;
    }).length;
    if (pool.length > shown.length || prodOnly) {
      h += `<div class="chartNote">${
        pool.length > shown.length
          ? chrono
            ? `most recent ${shown.length} of ${pool.length} weeks — full list in the table below`
            : `top ${shown.length} of ${pool.length} groups by spend — full list in the table below`
          : ""
      }${pool.length > shown.length && prodOnly ? " · " : ""}${
        prodOnly ? `${prodOnly} groups produced but not running (in the table)` : ""
      }</div>`;
    }
    chartEl.innerHTML = h;
    for (const el of chartEl.querySelectorAll<HTMLElement>(".grp"))
      el.addEventListener("click", () => {
        const agg2 = CURR_ROWS;
        const key = el.dataset.r;
        if (!agg2 || !key) return;
        const c = agg2.cells.get(key);
        const entry = agg2.rowList.find((x) => x.k === key);
        if (!c || !entry) return;
        openSideCell(c, entry.label, "all " + dimLabel(colDim));
      });
    renderRankTable();
  }

  function render(): void {
    emitState();
    const rank = view === "chart";
    vChart.classList.toggle("on", rank);
    vGrid.classList.toggle("on", !rank);
    tilesEl.style.display = rank ? "" : "none";
    chartEl.style.display = rank ? "" : "none";
    metricChipsEl.style.display = rank ? "" : "none";
    tblView.style.display = rank ? "" : "none";
    // The payback legend explains heat-map shading — grid view only.
    legendEl.style.display = rank ? "none" : "";
    gridWrap.style.display = rank ? "none" : "";
    colWrap.style.display = rank ? "none" : "";
    rowLbl.textContent = rank ? "Rank by" : "Rows";
    updateControlChips();
    if (rank) renderRankings();
    else renderGrid();
  }

  function renderGrid(): void {
    const agg = gridAgg();
    const { cells, rowList, colList } = agg;
    const colsShown = colList.slice(0, COL_CAP);
    const qy = rowQuery.trim().toLowerCase();
    const rowPool = qy ? rowList.filter((r) => r.label.toLowerCase().includes(qy)) : rowList;
    const rowsShown = showAllRows ? rowPool : rowPool.slice(0, ROW_CAP);
    let h =
      "<tr><th></th>" +
      colsShown
        .map((c) => `<th class="colhead" title="${escapeHtml(c.label)}">${escapeHtml(c.label)}</th>`)
        .join("") +
      "</tr>";
    for (const r of rowsShown) {
      h += `<tr><td class="rowhead" title="${escapeHtml(r.label)}">${escapeHtml(r.label)}</td>`;
      for (const c of colsShown) {
        const cell = cells.get(r.k + "|" + c.k);
        const f = cell ? fillFor(cell) : null;
        if (!cell || !f) {
          h += `<td class="cell empty"></td>`;
          continue;
        }
        if (f === "idle") {
          h += `<td class="cell idle" data-r="${escapeHtml(r.k)}" data-c="${escapeHtml(c.k)}">
          <div class="r1">${cell.produced} made</div>
          <div class="r2">not running</div></td>`;
          continue;
        }
        const badges = (cell.w ? `🏆${cell.w} ` : "") + (cell.l ? `✖${cell.l}` : "");
        h += `<td class="cell${cell.thin ? " thin" : ""}" data-r="${escapeHtml(r.k)}"
        data-c="${escapeHtml(c.k)}" style="background:${f};color:${inkFor(f)}">
        <div class="r1">${fmtRatio(cell.roas)}${cell.trend ? " " + cell.trend : ""}</div>
        <div class="r2">${escapeHtml(fmtMoney(cell.sp))} · ${cell.liveN} live` +
          (cell.produced ? ` · ${cell.produced} made` : "") +
          `</div><div class="badges">${badges}</div></td>`;
      }
      h += "</tr>";
    }
    mapEl.innerHTML = h;
    if (rowList.length > ROW_CAP) {
      moreBtn.style.display = "";
      moreBtn.textContent = showAllRows ? "Show top rows only" : `Show all ${rowList.length} rows`;
    } else moreBtn.style.display = "none";
    for (const td of root.querySelectorAll<HTMLTableCellElement>("td.cell:not(.empty)")) {
      td.addEventListener("mousemove", (ev) => showTip(ev, td));
      td.addEventListener("mouseleave", () => {
        tipEl.style.display = "none";
      });
      td.addEventListener("click", () => openSide(td));
    }
  }

  function rcLabels(td: HTMLElement): [string, string] {
    const r = CUR?.rowList.find((x) => x.k === td.dataset.r);
    const c = CUR?.colList.find((x) => x.k === td.dataset.c);
    return [r?.label || "?", c?.label || "?"];
  }

  function showTip(ev: MouseEvent, td: HTMLElement): void {
    const cell = CUR?.cells.get(`${td.dataset.r}|${td.dataset.c}`);
    if (!cell) return;
    const [rl, cl] = rcLabels(td);
    tipEl.innerHTML =
      `<div class="t">${escapeHtml(rl)} × ${escapeHtml(cl)}</div>` +
      (cell.sp
        ? `ROAS: <b>${fmtRatio(cell.roas)}</b>
      (${fmtRatio(cell.vs)}× account avg) · ${escapeHtml(fmtMoney(cell.sp))} ·
      ${cell.pu} purchases · ${cell.liveN} live<br>`
        : "No live spend in window.<br>") +
      (cell.proas !== null
        ? `Prior window: ${escapeHtml(fmtMoney(cell.psp))} at
      ${cell.proas.toFixed(2)} ROAS ${cell.trend || ""}<br>`
        : "") +
      `${cell.produced} produced` +
      (cell.w ? ` · 🏆 ${cell.w} sheet winners` : "") +
      (cell.l ? ` · ✖ ${cell.l} losers` : "") +
      (cell.thin ? "<br>⚠ thin spend — directional only" : "");
    tipEl.style.display = "block";
    tipEl.style.left = Math.min(ev.clientX + 14, window.innerWidth - 400) + "px";
    tipEl.style.top = ev.clientY + 14 + "px";
  }

  function openSide(td: HTMLElement): void {
    const cell = CUR?.cells.get(`${td.dataset.r}|${td.dataset.c}`);
    if (!cell) return;
    const [rl, cl] = rcLabels(td);
    openSideCell(cell, rl, cl);
  }

  function openSideCell(cell: Cell, rl: string, cl: string): void {
    let h = `<h2>${escapeHtml(rl)} × ${escapeHtml(cl)}</h2>`;
    h += cell.sp
      ? `<div>ROAS <b>${fmtRatio(cell.roas)}</b>
    (${fmtRatio(cell.vs)}× avg) · ${escapeHtml(fmtMoney(cell.sp))} · ${cell.pu}
    purchases</div>`
      : "<div>No live spend in window.</div>";
    if (cell.top.length) {
      h += "<h3>Top live creatives (window)</h3>";
      for (const t of cell.top) {
        const link = safeUrl(t.lk);
        const name = link
          ? `<a href="${escapeHtml(link)}" target="_blank" rel="noopener">${escapeHtml(t.lb)} ↗</a>`
          : escapeHtml(t.lb);
        const hl = t.d.headline ? `<div class="m">“${escapeHtml(t.d.headline)}”</div>` : "";
        const thumb = safeThumb(t.th);
        h += `<div class="item">${
          thumb ? `<img class="thumb" src="${escapeHtml(thumb)}" alt="">` : ""
        }${name}${hl}
        <div class="m">${escapeHtml(fmtMoney(t.sp))} ·
        back/$1 ${t.sp ? (t.val / t.sp).toFixed(2) : "—"} · ${t.pu} purch
        </div></div>`;
      }
    }
    if (cell.concepts.length) {
      h += `<h3>Produced (${cell.produced} rows, showing
      ${cell.concepts.length})</h3>`;
      for (const k of cell.concepts) {
        const pill =
          k.rb === "w"
            ? `<span class="pill win">winner</span> `
            : k.rb === "l"
              ? `<span class="pill los">loser</span> `
              : "";
        const conceptText = `<b>${escapeHtml(k.d.concept || "(no concept)")}</b>`;
        const adLink = safeUrl(k.la);
        const cname = adLink
          ? `<a href="${escapeHtml(adLink)}" target="_blank" rel="noopener">${conceptText} ↗</a>`
          : conceptText;
        const briefLink = safeUrl(k.lbr);
        const brief = briefLink
          ? ` · <a href="${escapeHtml(briefLink)}" target="_blank" rel="noopener">brief ↗</a>`
          : "";
        h += `<div class="item">${pill}${cname}
        <div class="m">${[k.d.week, k.d.visual_type, k.d.solution_mechanism, k.who, k.st]
          .filter(Boolean)
          .map((x) => escapeHtml(x))
          .join(" · ")}${brief}</div>${
          k.rs ? `<div class="m">${escapeHtml(k.rs)}</div>` : ""
        }${k.lr ? `<div class="m">💡 ${escapeHtml(k.lr)}</div>` : ""}</div>`;
      }
    }
    sideBody.innerHTML = h;
    sideEl.classList.add("open");
  }

  /**
   * The ranking table (MagicBrief-style): one row per group of the ranked
   * dimension, one colour-chipped column per active metric, sortable by
   * clicking a metric header, best value per highlight-metric washed green.
   */
  function renderRankTable(): void {
    const { cells, rowList } = rowsAgg();
    const on = METRICS.filter((m) => metricsOn.has(m.id));
    const qy = rowQuery.trim().toLowerCase();
    // The table also carries produced-but-not-running groups (sp 0) — that
    // coverage honesty is the tool's point; the chart shows spenders only.
    const pool = rowList.filter((r) => {
      const c = cells.get(r.k);
      return !!c && (c.sp > 0 || c.produced > 0) && (!qy || r.label.toLowerCase().includes(qy));
    });
    const sortM = METRICS.find((m) => m.id === tableSort && metricsOn.has(m.id)) ?? on[0];
    const chrono = chronoWeeks && rowDim === "week";
    const rows = chrono
      ? [...pool].sort((a, b) => weekOrd(a.label) - weekOrd(b.label))
      : [...pool].sort((a, b) => {
          const av = sortM.val(cells.get(a.k)!) ?? -1;
          const bv = sortM.val(cells.get(b.k)!) ?? -1;
          return bv - av;
        });
    const shown = showAllRows ? rows : rows.slice(0, TABLE_CAP);
    const bestOf = new Map<string, number>();
    for (const m of on) {
      if (!m.highlight) continue;
      let best = 0;
      for (const r of rows) {
        const v = m.val(cells.get(r.k)!);
        if (v !== null && v > best) best = v;
      }
      bestOf.set(m.id, best);
    }
    let h = `<div class="tblcap"><span class="t">${escapeHtml(dimLabel(rowDim))} rankings</span>
    <span class="m">${
      chrono
        ? "chronological (oldest first) · green = best · click a row for detail"
        : "click a column to sort · green = best · click a row for detail"
    }</span></div>
    <table><tr><th>${escapeHtml(dimLabel(rowDim))}</th>`;
    METRICS.forEach((m, i) => {
      if (!metricsOn.has(m.id)) return;
      h += `<th class="msort${!chrono && m.id === sortM.id ? " on" : ""}" data-m="${m.id}"${
        m.tip ? ` title="${escapeHtml(m.tip)}"` : ""
      }>
      <span class="idx" style="background:${m.color}">${i + 1}</span>${m.label}${
        !chrono && m.id === sortM.id ? " ↓" : ""
      }</th>`;
    });
    h += "</tr>";
    for (const r of shown) {
      const c = cells.get(r.k)!;
      const topThumb = c.top.map((t) => safeThumb(t.th)).find((u) => u !== null) || null;
      h += `<tr class="rankRow" data-r="${escapeHtml(r.k)}"><td class="who">${
        topThumb ? `<img src="${escapeHtml(topThumb)}" alt="">` : ""
      }<span class="whoText"><b>${escapeHtml(r.label)}</b><i>${c.liveN} live · ${c.produced} made${
        c.thin ? " · thin spend" : ""
      }</i></span></td>`;
      for (const m of on) {
        const v = m.val(c);
        const best = m.highlight && v !== null && v > 0 && v === bestOf.get(m.id);
        const trend = m.id === "roas" && c.trend ? " " + c.trend : "";
        h += `<td class="${best ? "best" : ""}">${
          v === null || v === 0 ? "—" : escapeHtml(m.fmt(v)) + trend
        }</td>`;
      }
      h += "</tr>";
    }
    h += "</table>";
    tblView.innerHTML = h;
    for (const th of tblView.querySelectorAll<HTMLElement>("th.msort"))
      th.addEventListener("click", () => {
        tableSort = th.dataset.m || "sp";
        chronoWeeks = false; // a metric sort overrides chronological order
        render(); // chart + chip follow the same ordering
        emitState();
      });
    for (const tr of tblView.querySelectorAll<HTMLElement>("tr.rankRow"))
      tr.addEventListener("click", () => {
        const agg2 = CURR_ROWS;
        const key = tr.dataset.r;
        if (!agg2 || !key) return;
        const c = agg2.cells.get(key);
        const entry = agg2.rowList.find((x) => x.k === key);
        if (!c || !entry) return;
        openSideCell(c, entry.label, "all " + dimLabel(colDim));
      });
    if (rows.length > TABLE_CAP) {
      moreBtn.style.display = "";
      moreBtn.textContent = showAllRows
        ? "Show top groups only"
        : `Show all ${rows.length} groups`;
    } else {
      moreBtn.style.display = "none";
    }
  }

  /* ── controls ── */
  function fillSelect(sel: HTMLSelectElement, withNone = false): void {
    // DIMS is a code constant (trusted) — the only non-data interpolation.
    sel.innerHTML =
      (withNone ? '<option value="">— none —</option>' : "") +
      DIMS.map((d) => `<option value="${d[0]}">${d[1]}</option>`).join("");
  }

  /** The colour-coded metric chip row (MagicBrief-style numbered chips). */
  function renderMetricChips(): void {
    metricChipsEl.innerHTML = "";
    METRICS.forEach((m, i) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "mchip" + (metricsOn.has(m.id) ? " on" : "");
      // METRICS is a code constant (trusted markup).
      b.innerHTML = `<span class="idx" style="background:${m.color}">${i + 1}</span>${m.label}`;
      b.onclick = () => {
        if (metricsOn.has(m.id)) {
          if (metricsOn.size === 1) return; // always keep at least one metric
          metricsOn.delete(m.id);
        } else {
          metricsOn.add(m.id);
        }
        renderMetricChips();
        renderRankings();
        emitState();
      };
      metricChipsEl.appendChild(b);
    });
  }

  // No in-module product select (2026-07-23): the page's segmented control
  // owns product switching (it remounts, or calls setProduct below).
  function switchProduct(id: string): void {
    prod = id;
    showAllRows = false;
    updateHeader();
    render();
  }

  fillSelect(rowSel);
  fillSelect(colSel);
  rowSel.value = rowDim;
  colSel.value = colDim;
  rowSel.onchange = () => {
    if (dimOk(rowSel.value)) rowDim = rowSel.value;
    if (colDim === rowDim) {
      colDim = firstDimExcept(rowDim);
      colSel.value = colDim;
    }
    showAllRows = false;
    render();
  };
  colSel.onchange = () => {
    if (dimOk(colSel.value)) colDim = colSel.value;
    if (rowDim === colDim) {
      rowDim = firstDimExcept(colDim);
      rowSel.value = rowDim;
    }
    showAllRows = false;
    render();
  };
  moreBtn.onclick = () => {
    showAllRows = !showAllRows;
    render();
  };
  rtChip.onclick = () => {
    // included → excluded → only → included
    segment = segment === "all" ? "pros" : segment === "pros" ? "ret" : "all";
    updateHeader();
    render();
    emitState();
  };
  chronoChip.onclick = () => {
    chronoWeeks = !chronoWeeks;
    render();
  };
  rowSearch.oninput = () => {
    rowQuery = rowSearch.value;
    render();
  };
  vChart.onclick = () => {
    view = "chart";
    render();
  };
  vGrid.onclick = () => {
    view = "grid";
    render();
  };
  q<HTMLButtonElement>(".sideX").onclick = () => sideEl.classList.remove("open");
  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") sideEl.classList.remove("open");
  };
  document.addEventListener("keydown", onKeydown);

  renderMetricChips();
  updateHeader();
  render();

  let destroyed = false;
  return {
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      document.removeEventListener("keydown", onKeydown);
      root.remove();
    },
    setProduct(id: string): void {
      if (destroyed || !products[id] || id === prod) return;
      switchProduct(id);
    },
  };
}
